import { db } from "../lib/firebase";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
} from "../lib/firestoreClient";
import { sanitizeId, sanitizeText } from "../utils/sanitize";
import { errorDev } from '../utils/logger.js';

export const REPORT_ENTITY_TYPES = {
  PROFILE: "profile",
  PRODUCT: "product",
};

export const REPORT_REASONS = {
  INAPPROPRIATE: "inappropriate_content",
  SPAM: "spam",
  FRAUD: "fraud_or_scam",
  HARASSMENT: "harassment_or_abuse",
  MISLEADING: "misleading_information",
  OTHER: "other",
};

const COLLECTIONS = {
  REPORTS: "reports",
};

const VALID_ENTITY_TYPES = new Set(Object.values(REPORT_ENTITY_TYPES));
const VALID_REASONS = new Set(Object.values(REPORT_REASONS));

function assertAuthenticatedUser(user) {
  if (!user || !user.uid) throw new Error("Authentication required");
  return user;
}

function normalizeEntityType(entityType) {
  const safeType = sanitizeText(entityType, 20).toLowerCase();
  if (!VALID_ENTITY_TYPES.has(safeType)) {
    throw new Error("Invalid report entity type");
  }
  return safeType;
}

function normalizeReason(reason) {
  const safeReason = sanitizeText(reason, 60).toLowerCase();
  if (!VALID_REASONS.has(safeReason)) {
    throw new Error("Invalid report reason");
  }
  return safeReason;
}

async function hasRecentOpenReport({ reporterId, entityType, entityId }) {
  try {
    const recentByUserQ = query(
      collection(db, COLLECTIONS.REPORTS),
      where("reporterId", "==", reporterId),
      limit(20)
    );

    const snap = await getDocs(recentByUserQ);
    const nowMs = Date.now();

    return snap.docs.some((row) => {
      const data = row.data() || {};
      if (String(data.entityType || "") !== entityType) return false;
      if (String(data.entityId || "") !== entityId) return false;
      if (!["open", "under_review"].includes(String(data.status || "open"))) return false;

      const createdAtMs = data.createdAt?.toDate?.()?.getTime() || 0;
      const elapsedMinutes = (nowMs - createdAtMs) / 60000;

      return elapsedMinutes >= 0 && elapsedMinutes <= 30;
    });
  } catch (err) {
    // Duplicate protection is best-effort and should never block reporting.
    errorDev("Operation failed:", err);
    return false;
  }
}

export async function submitEntityReport({
  reporter,
  entityType,
  entityId,
  reason,
  details = "",
  entityOwnerId = null,
  metadata = {},
}) {
  const user = assertAuthenticatedUser(reporter);
  const normalizedEntityType = normalizeEntityType(entityType);
  const normalizedReason = normalizeReason(reason);
  const safeEntityId = sanitizeId(entityId);

  if (!safeEntityId) throw new Error("Invalid entity id");

  const safeDetails = sanitizeText(details, 1200);
  if (safeDetails.length < 10) {
    throw new Error("Please provide more details about the violation");
  }

  const hasRecentDuplicate = await hasRecentOpenReport({
    reporterId: user.uid,
    entityType: normalizedEntityType,
    entityId: safeEntityId,
  });

  if (hasRecentDuplicate) {
    throw new Error("You have already reported this content recently");
  }

  const docRef = await addDoc(collection(db, COLLECTIONS.REPORTS), {
    reporterId: user.uid,
    reporterEmail: user.email || "",
    reporterName: user.displayName || "",
    entityType: normalizedEntityType,
    entityId: safeEntityId,
    entityOwnerId: sanitizeId(entityOwnerId) || null,
    reason: normalizedReason,
    details: safeDetails,
    status: "open",
    metadata: metadata && typeof metadata === "object" ? metadata : {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return { id: docRef.id };
}
